import React, { FC, useEffect, useRef } from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { getChildClinicsNamesWithParentTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { useTranslation } from 'react-i18next';

type LabDefaultClinicAutocompleteProps = {
    mainClinicId: string;
    value?: string;
    onChange: (clinicId: string | null) => void;
    preferredClinicId?: string;
};

/**
 * Lets a lab record which child clinic a dentist belongs to, for a main organization
 * that has child clinics.
 *
 * Renders nothing at all — including its own grid cell, so no blank space is reserved —
 * unless the scoped clinic is a main/parent clinic that actually has child clinics to
 * choose between. Uses the same `showOnlyDeliveryLocations` option set as the order
 * flow's delivery clinic dropdown, so a stored value is always selectable there.
 *
 * `preferredClinicId` is a suggestion from elsewhere in the UI, not a value: it is only
 * selected once the child clinic turns out to be among this dropdown's own options, so a
 * clinic this dropdown cannot offer never ends up stored behind an empty-looking field.
 * It is applied at most once, so the lab can still clear or change it afterward.
 */
const LabDefaultClinicAutocomplete: FC<LabDefaultClinicAutocompleteProps> = ({
    mainClinicId,
    value,
    onChange,
    preferredClinicId,
}: LabDefaultClinicAutocompleteProps) => {
    const { t: translate } = useTranslation(['users']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { childClinicsWithParent } = {}, loading } = useQuery(getChildClinicsNamesWithParentTemplate, {
        skip: !mainClinicId,
        variables: { parentClinicId: mainClinicId, showOnlyDeliveryLocations: true },
        onError(error) {
            showBoundary(error);
        },
    });

    // The query returns the main organization itself alongside its children, and only
    // a child clinic is a valid value here.
    const childClinics: Organization[] = (childClinicsWithParent || []).filter(
        (clinic: Organization) => clinic._id !== mainClinicId,
    );

    const hasAppliedPreferredRef = useRef(false);
    const canApplyPreferred =
        !!preferredClinicId && !value && childClinics.some((clinic) => clinic._id === preferredClinicId);

    useEffect(() => {
        if (hasAppliedPreferredRef.current || !canApplyPreferred || !preferredClinicId) {
            return;
        }
        hasAppliedPreferredRef.current = true;
        onChange(preferredClinicId);
    }, [canApplyPreferred, preferredClinicId, onChange]);

    // Nothing to choose between: either still loading, or this clinic is not a main
    // clinic with children. Return null rather than an empty element so the enclosing
    // grid does not reserve a cell for a field that is never shown.
    if (loading || !childClinics.length) {
        return null;
    }

    const selectedClinic = childClinics.find((clinic) => clinic._id === value) || null;

    return (
        <Grid item xs={12} md={6}>
            <Autocomplete
                id="lab-default-clinic"
                options={childClinics}
                value={selectedClinic}
                getOptionLabel={(clinic: Organization) => clinic.name || ''}
                getOptionKey={(clinic: Organization) => clinic._id}
                isOptionEqualToValue={(option: Organization, selected: Organization) => option?._id === selected?._id}
                onChange={(_event, clinic) => onChange(clinic ? clinic._id : null)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        label={translate('users:LAB_DEFAULT_CLINIC')}
                        helperText={translate('users:LAB_DEFAULT_CLINIC_INFO')}
                        data-test="lab-default-clinic-autocomplete.input"
                    />
                )}
                fullWidth
            />
        </Grid>
    );
};

export default LabDefaultClinicAutocomplete;
